import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { Image } from '@lumapps/lumx-images/types';
import { SortableList } from '@lumapps/lumx-lists/components/SortableList';
import { SortableListProps } from '@lumapps/lumx-lists/components/SortableList/types';
import { mdiDrag } from '@lumapps/lumx/icons';
import { Badge, ColorPalette, FlexBox, Icon, Size, Text } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { WREX_IMAGE_GALLERY } from '../../keys';

import './index.scss';

export interface ImageDraggableGridProps {
    /** The images array state */
    images: Image[];
    /** Callback when an image is focused */
    onResourceFocus?: (index: number) => void;
    /** Callback when typing enter on an image */
    onResourceEnter?: () => void;
    /** Set the images array state (used to reorder the images) */
    setImages: React.Dispatch<React.SetStateAction<Image[]>>;
    /** The images array as JSX elements (used to display the grid) */
    resourcesItems: React.JSX.Element[];
    /** The width of the grid */
    horizontalSize?: number;
    /** Set the images array as JSX elements (used when reordering the images) */
    setResourcesItems?: React.Dispatch<React.SetStateAction<React.JSX.Element[]>>;
}

const CLASSNAME = 'image-draggable-grid';

/**
 * Component that displays a draggable grid of images.
 * The grid is used in the image gallery dialog to allow the user to change the order of the images.
 *
 * @param ImageDraggableGridProps
 * @returns ImageDraggableGrid
 */
export const ImageDraggableGrid: React.FC<ImageDraggableGridProps> = ({
    images,
    onResourceFocus,
    onResourceEnter,
    setImages,
    resourcesItems,
    horizontalSize,
    setResourcesItems,
}) => {
    const { translateAndReplace } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);

    const onMove: SortableListProps<Image>['onMove'] = (newImages, oldIndex, newIndex) => {
        setImages(newImages);
        // Reorder resourcesItems accordingly
        const newResourcesItems = [...resourcesItems];
        const [removed] = newResourcesItems.splice(oldIndex, 1);
        newResourcesItems.splice(newIndex, 0, removed);
        setResourcesItems?.(newResourcesItems);
        onResourceFocus?.(newIndex);
    };

    const onFocus = (index: number) => {
        onResourceFocus?.(index);
    };

    const entityRenderer: SortableListProps<Image>['entityRenderer'] = React.useCallback(
        (image, { index, isDragged }) => {
            return (
                <div
                    className={element('image-wrapper', { 'is-dragging': !!isDragged })}
                    aria-label={translateAndReplace(WREX_IMAGE_GALLERY.IMAGE_GALLERY_DIALOG_IMAGE_ARIA_LABEL, {
                        INDEX: index + 1,
                    })}
                >
                    {isDragged ? (
                        <FlexBox className={element('image-dark-rectangle')} hAlign="center" vAlign="center">
                            <Icon
                                // eslint-disable-next-line lumapps/no-classname-strings
                                className="image-dark-rectangle__icon"
                                icon={mdiDrag}
                                size={Size.l}
                                color={ColorPalette.light}
                            />
                        </FlexBox>
                    ) : (
                        <Badge className={element('badge')} color={ColorPalette.light}>
                            <Text as="span">{index + 1}</Text>
                        </Badge>
                    )}
                    {resourcesItems[index]}
                </div>
            );
        },
        [element, resourcesItems, translateAndReplace],
    );

    return (
        <SortableList
            className={block()}
            mode="grid"
            columns={horizontalSize}
            gap={Size.big}
            entities={images}
            entityRenderer={entityRenderer}
            onMove={onMove}
            onFocus={onFocus}
            onEnter={onResourceEnter}
        />
    );
};
